import {
  CanvasSizeType,
  CircularPathType,
  gapInXAxis,
  LabelType,
  NodeType,
  PathType,
} from '../constants';
import {
  getPathsOfGridColumnWise,
  getPointsInCircle,
  getPointsInGridWithGridLabels,
} from '../utils';

type PointsListType = {
  pathType: PathType;
  nodes: NodeType[];
  paths: number[][] | CircularPathType[];
  labels: LabelType[];
  pinRadius: number;
};

/**
This method returns the paths/lines and node points of Actor Puck
**/
const getActorPoints = (canvasSize: CanvasSizeType): PointsListType => {
  const { width, height } = canvasSize;
  const pinDiameter = Math.min(canvasSize.width, canvasSize.height) * 0.12;
  const pinRadius = pinDiameter / 2;
  const xStart = width / 2;
  const gapInYAxis = height / 9;
  const yStart = gapInYAxis;
  return {
    pathType: PathType.STRAIGHT,
    nodes: [
      { x: xStart - pinDiameter - gapInXAxis, y: yStart },
      { x: xStart + gapInXAxis, y: yStart },

      { x: xStart - 2 * pinDiameter - gapInXAxis, y: yStart + pinDiameter + gapInYAxis },
      { x: xStart - pinRadius, y: yStart + pinDiameter + gapInYAxis },
      { x: xStart + pinDiameter + gapInXAxis, y: yStart + pinDiameter + gapInYAxis },

      {
        x: xStart - 2 * pinDiameter - 3 * gapInXAxis,
        y: yStart + 2 * pinDiameter + 2 * gapInYAxis,
      },
      {
        x: xStart - pinDiameter - gapInXAxis,
        y: yStart + 2 * pinDiameter + 2 * gapInYAxis,
      },
      { x: xStart + gapInXAxis, y: yStart + 2 * pinDiameter + 2 * gapInYAxis },
      {
        x: xStart + pinDiameter + 3 * gapInXAxis,
        y: yStart + 2 * pinDiameter + 2 * gapInYAxis,
      },

      {
        x: xStart - 2 * pinDiameter - gapInXAxis,
        y: yStart + 3 * pinDiameter + 3 * gapInYAxis,
      },
      { x: xStart - pinRadius, y: yStart + 3 * pinDiameter + 3 * gapInYAxis },
      {
        x: xStart + pinDiameter + gapInXAxis,
        y: yStart + 3 * pinDiameter + 3 * gapInYAxis,
      },
    ],
    paths: [
      [
        xStart - 2 * gapInXAxis - pinDiameter,
        5,

        xStart + 2 * gapInXAxis + pinDiameter,
        5,

        xStart + 2 * pinDiameter + 5.5 * gapInXAxis,
        yStart + 2 * pinDiameter + 2 * gapInYAxis + pinRadius,

        xStart + 2 * pinDiameter + 2 * gapInXAxis,
        yStart + 3.5 * pinDiameter + 4 * gapInYAxis,

        xStart - 2 * pinDiameter - 2 * gapInXAxis,
        yStart + 3.5 * pinDiameter + 4 * gapInYAxis,

        xStart - 2 * pinDiameter - 5.5 * gapInXAxis,
        yStart + 2 * pinDiameter + 2 * gapInYAxis + pinRadius,

        xStart - 2 * gapInXAxis - pinDiameter,
        5,
      ],

      [
        xStart - gapInXAxis - pinRadius,
        yStart + pinRadius,

        xStart + gapInXAxis + pinRadius,
        yStart + pinRadius,

        xStart + pinDiameter + pinRadius + 3 * gapInXAxis,
        yStart + 2 * pinDiameter + pinRadius + 2 * gapInYAxis,

        xStart + pinDiameter + pinRadius + gapInXAxis,
        yStart + 3 * pinDiameter + pinRadius + 3 * gapInYAxis,

        xStart,
        yStart + 3 * pinDiameter + pinRadius + 3 * gapInYAxis,

        xStart - pinDiameter - pinRadius - gapInXAxis,
        yStart + 3 * pinDiameter + pinRadius + 3 * gapInYAxis,

        xStart - pinDiameter - pinRadius - 3 * gapInXAxis,
        yStart + 2 * pinDiameter + pinRadius + 2 * gapInYAxis,

        xStart - gapInXAxis - pinRadius,
        yStart + pinRadius,
      ],

      [
        xStart,
        yStart + pinDiameter + pinRadius + gapInYAxis,
        xStart + gapInXAxis + pinRadius,
        yStart + 2 * pinDiameter + pinRadius + 2 * gapInYAxis,
        xStart - gapInXAxis - pinRadius,
        yStart + 2 * pinDiameter + pinRadius + 2 * gapInYAxis,
        xStart,
        yStart + pinDiameter + pinRadius + gapInYAxis,
      ],
    ],
    labels: [],
    pinRadius,
  };
};

/**
This method returns the paths/lines and node points of Spine Puck
**/
const getSpinePoints = (canvasSize: CanvasSizeType): PointsListType => {
  const { width, height } = canvasSize;
  const pinDiameter = Math.min(canvasSize.width, canvasSize.height) * 0.12;
  const pinRadius = pinDiameter / 2;
  const xStart = width / 2;
  const yStart = height / 2;
  const outerCircleRadius = Math.min(width, height) / 2 - pinRadius;
  return {
    pathType: PathType.CIRCULAR,
    nodes: getPointsInCircle(
      xStart,
      yStart,
      outerCircleRadius - pinDiameter,
      10,
      -120,
      pinRadius,
    ),
    paths: [
      {
        radius: outerCircleRadius,
        x: xStart,
        y: yStart,
      },
      {
        radius: outerCircleRadius - pinDiameter,
        x: xStart,
        y: yStart,
      },
    ],
    labels: [],
    pinRadius,
  };
};

/**
This method returns the paths/lines and node points of Uni Puck
**/
const getUniPoints = (canvasSize: CanvasSizeType): PointsListType => {
  const { width, height } = canvasSize;
  const pinDiameter = Math.min(canvasSize.width, canvasSize.height) * 0.12;
  const pinRadius = pinDiameter / 2;
  const xStart = width / 2;
  const yStart = height / 2;
  const outerCircleRadius = Math.min(width, height) / 2 - pinRadius;
  return {
    pathType: PathType.CIRCULAR,
    nodes: [
      ...getPointsInCircle(
        xStart,
        yStart,
        outerCircleRadius - 2.5 * pinDiameter,
        5,
        -90,
        pinRadius,
      ),
      ...getPointsInCircle(
        xStart,
        yStart,
        outerCircleRadius - pinDiameter,
        11,
        -120,
        pinRadius,
      ),
    ],
    paths: [
      {
        radius: outerCircleRadius,
        x: xStart,
        y: yStart,
      },
      {
        radius: outerCircleRadius - pinDiameter,
        x: xStart,
        y: yStart,
      },
      {
        radius: outerCircleRadius - 2.5 * pinDiameter,
        x: xStart,
        y: yStart,
      },
    ],
    labels: [],
    pinRadius,
  };
};

/**
This method returns the paths/lines and node points of sslr Puck
**/
const getSslrPoints = (canvasSize: CanvasSizeType): PointsListType => {
  const [requiredRows, requiredColumns] = [12, 8];
  const { width, height } = canvasSize;
  const pinDiameter = Math.min(canvasSize.width, canvasSize.height) * 0.09;
  const [points, labels] = getPointsInGridWithGridLabels(
    width,
    height,
    requiredRows,
    requiredColumns,
    pinDiameter,
  );
  return {
    pathType: PathType.STRAIGHT,
    nodes: points,
    paths: getPathsOfGridColumnWise(
      width,
      height,
      requiredRows,
      requiredColumns,
      pinDiameter,
    ),
    labels: labels,
    pinRadius: pinDiameter / 2,
  };
};
export { getActorPoints, getSpinePoints, getUniPoints, getSslrPoints };
